import { useMemo } from 'react';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { ProductKey } from '@/components/signup/utils/products';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';

import { PerceivedProvisioningStartTimer } from '../../utils/perceived-provisioning/perceived-provisioning-start-timer';
import { defaultScreenConfig } from '../../utils/screen-manager/default-screen-config';
import { teamChoiceDictionaryItem } from '../../utils/screen-manager/default-screen-dictionary';
import {
	JSM_USE_CASE_PROVISIONING,
	JSM_USE_CASE_TEMPLATE_CHOICE,
	PROVISIONING,
} from '../../utils/screen-manager/utils';
import { JsmUseCaseProvisioningScreen } from '../jsm-use-case-provisioning/jsm-use-case-provisioning-screen';
import { JsmUseCaseTemplateChoiceScreen } from '../jsm-use-case-template-choice/jsm-use-case-template-choice-screen';
import { ProductRedirector } from '../product-redirector/product-redirector';
import ServiceCollectionProvisioningScreen from '../service-collection-provisioning-screen/service-collection-provisioning-screen-lofi';

import { BaseProvisioningUI } from './base-provisioning-ui';

const serviceCollectionScreenDictionary = {
	...teamChoiceDictionaryItem,
	[PROVISIONING]: (
		<PerceivedProvisioningStartTimer>
			<ProductRedirector>
				<ServiceCollectionProvisioningScreen />
			</ProductRedirector>
		</PerceivedProvisioningStartTimer>
	),
};

const noop = () => undefined;

const jsmUseCaseTemplateChoiceConfig = {
	[JSM_USE_CASE_TEMPLATE_CHOICE]: {
		position: { default: 200 },
		visibility: { default: true },
		onMount: noop,
	},
	[JSM_USE_CASE_PROVISIONING]: {
		position: { default: 300 },
		visibility: { default: true },
		onMount: noop,
	},
};

const jsmUseCaseServiceCollectionScreenDictionary = {
	...teamChoiceDictionaryItem,
	[JSM_USE_CASE_TEMPLATE_CHOICE]: <JsmUseCaseTemplateChoiceScreen />,
	[JSM_USE_CASE_PROVISIONING]: <JsmUseCaseProvisioningScreen />,
};

/**
 * Component that shows Service Collection provisioning UI with team choice support.
 * @returns @type {JSX.Element}
 */
export const ServiceCollectionProvisioningUI = () => {
	const { products = [], primaryCollectionProduct } = useSearchParameters();
	const jsmGrowth519Result = useExperiment(experiments.jsmGrowth519JsmLandUseCaseOnboarding);
	const isJsmExperimentEnabled =
		jsmGrowth519Result.type === 'enrolled'
			? jsmGrowth519Result.value
			: experiments.jsmGrowth519JsmLandUseCaseOnboarding.defaultValue;
	const shouldUseJsmUseCaseTemplateChoice =
		isJsmExperimentEnabled &&
		products.includes(ProductKey.SERVICE_COLLECTION) &&
		primaryCollectionProduct === ProductKey.JIRA_SERVICE_MANAGEMENT;

	const screenConfig = useMemo(
		() =>
			shouldUseJsmUseCaseTemplateChoice
				? {
						...defaultScreenConfig,
						...jsmUseCaseTemplateChoiceConfig,
					}
				: defaultScreenConfig,
		[shouldUseJsmUseCaseTemplateChoice],
	);

	const screenDictionary = useMemo(
		() =>
			shouldUseJsmUseCaseTemplateChoice
				? jsmUseCaseServiceCollectionScreenDictionary
				: serviceCollectionScreenDictionary,
		[shouldUseJsmUseCaseTemplateChoice],
	);

	return (
		<BaseProvisioningUI
			screenConfig={screenConfig}
			screenDictionary={screenDictionary}
			screenFallback={
				<PerceivedProvisioningStartTimer>
					<ProductRedirector>
						<ServiceCollectionProvisioningScreen />
					</ProductRedirector>
				</PerceivedProvisioningStartTimer>
			}
		/>
	);
};
